import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminReleases } from '../services/api';
import {
  ArrowLeft,
  Tag,
  Calendar,
  Shield,
  Wrench,
  Sparkles,
  TrendingUp,
  XCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from '../icons';

const CATEGORY_META = {
  security: { label: 'Security', icon: Shield, color: '#dc2626', bg: '#fee2e2' },
  fixed: { label: 'Fixed', icon: Wrench, color: '#0891b2', bg: '#cffafe' },
  added: { label: 'Added', icon: Sparkles, color: '#16a34a', bg: '#dcfce7' },
  improved: { label: 'Improved', icon: TrendingUp, color: '#7c3aed', bg: '#ede9fe' },
  removed: { label: 'Removed', icon: XCircle, color: '#6b7280', bg: '#f3f4f6' },
};

const CATEGORY_ORDER = ['security', 'added', 'improved', 'fixed', 'removed'];

export default function AdminReleases() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAdminReleases()
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.releases || [];
        setReleases(list);
        // Expand the most recent release by default
        if (list.length) setExpanded({ [list[0].version]: true });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err.message || 'Failed to load releases');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, authLoading, navigate]);

  const filteredReleases = useMemo(() => {
    if (filter === 'all') return releases;
    return releases.filter((r) => r.changes && Array.isArray(r.changes[filter]) && r.changes[filter].length);
  }, [releases, filter]);

  const totals = useMemo(() => {
    const t = { releases: releases.length, security: 0, fixed: 0, added: 0, improved: 0, removed: 0 };
    releases.forEach((r) => {
      Object.keys(t).forEach((k) => {
        if (k === 'releases') return;
        if (r.changes && Array.isArray(r.changes[k])) t[k] += r.changes[k].length;
      });
    });
    return t;
  }, [releases]);

  const toggle = (version) => setExpanded((p) => ({ ...p, [version]: !p[version] }));

  if (authLoading || loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <button
          onClick={() => navigate('/admin')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: 4,
          }}
          aria-label="Back to admin dashboard"
        >
          <ArrowLeft size={18} /> Back
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, color: '#111827', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Tag size={26} style={{ color: '#DC143C' }} />
            Release audit log
          </h1>
          <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 14 }}>
            Every shipped change, grouped by release. Security fixes called out at the top of each entry.
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            cursor: 'pointer',
            color: '#374151',
            fontSize: 14,
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stat strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        {[
          { label: 'Releases', value: totals.releases, color: '#111827', bg: '#f3f4f6' },
          { label: 'Security', value: totals.security, color: '#dc2626', bg: '#fee2e2' },
          { label: 'Added', value: totals.added, color: '#16a34a', bg: '#dcfce7' },
          { label: 'Improved', value: totals.improved, color: '#7c3aed', bg: '#ede9fe' },
          { label: 'Fixed', value: totals.fixed, color: '#0891b2', bg: '#cffafe' },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderLeft: `4px solid ${s.color}`,
              borderRadius: 10,
              padding: '14px 16px',
            }}
          >
            <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color, marginTop: 2 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {[
          { key: 'all', label: 'All changes' },
          ...CATEGORY_ORDER.map((k) => ({ key: k, label: CATEGORY_META[k].label })),
        ].map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 999,
                border: '1px solid',
                borderColor: active ? '#DC143C' : '#e5e7eb',
                background: active ? '#DC143C' : '#fff',
                color: active ? '#fff' : '#374151',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div
          style={{
            padding: 14,
            background: '#fee2e2',
            color: '#991b1b',
            border: '1px solid #fecaca',
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {!error && filteredReleases.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', background: '#fff', border: '1px dashed #e5e7eb', borderRadius: 12 }}>
          No releases match this filter.
        </div>
      )}

      {/* Timeline */}
      <div style={{ position: 'relative' }}>
        {/* vertical rail */}
        <div
          style={{
            position: 'absolute',
            left: 16,
            top: 14,
            bottom: 14,
            width: 2,
            background: 'linear-gradient(to bottom, #DC143C, #fde68a 40%, #e5e7eb)',
            borderRadius: 2,
          }}
          aria-hidden
        />
        {filteredReleases.map((r) => (
          <ReleaseCard key={r.version} release={r} expanded={!!expanded[r.version]} onToggle={() => toggle(r.version)} filter={filter} />
        ))}
      </div>
    </div>
  );
}

function ReleaseCard({ release, expanded, onToggle, filter }) {
  const securityCount = release.changes?.security?.length || 0;
  const dateLabel = release.date
    ? new Date(release.date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  return (
    <div style={{ position: 'relative', paddingLeft: 48, marginBottom: 20 }}>
      {/* dot */}
      <div
        style={{
          position: 'absolute',
          left: 7,
          top: 18,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: securityCount > 0 ? '#dc2626' : '#DC143C',
          border: '4px solid #fff',
          boxShadow: '0 0 0 1px #e5e7eb',
        }}
        aria-hidden
      />
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <button
          onClick={onToggle}
          aria-expanded={expanded}
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '16px 20px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
              <span
                style={{
                  display: 'inline-block',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#DC143C',
                  background: '#fef2f2',
                  padding: '2px 8px',
                  borderRadius: 6,
                }}
              >
                {release.version}
              </span>
              {dateLabel && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#6b7280', fontSize: 13 }}>
                  <Calendar size={13} /> {dateLabel}
                </span>
              )}
              {securityCount > 0 && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#dc2626',
                    background: '#fee2e2',
                    padding: '2px 8px',
                    borderRadius: 999,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  <Shield size={11} /> {securityCount} security
                </span>
              )}
            </div>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>{release.title}</div>
            {release.summary && !expanded && (
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>{release.summary}</div>
            )}
          </div>
          <div style={{ flexShrink: 0, color: '#9ca3af' }}>
            {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </button>

        {expanded && (
          <div style={{ padding: '0 20px 20px', borderTop: '1px solid #f3f4f6' }}>
            {release.summary && (
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, margin: '14px 0 16px' }}>{release.summary}</p>
            )}

            {CATEGORY_ORDER.map((cat) => {
              const items = release.changes?.[cat];
              if (!items || items.length === 0) return null;
              if (filter !== 'all' && filter !== cat) return null;
              const meta = CATEGORY_META[cat];
              const Icon = meta.icon;
              return (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      color: meta.color,
                      background: meta.bg,
                      padding: '3px 10px',
                      borderRadius: 999,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      marginBottom: 8,
                    }}
                  >
                    <Icon size={12} /> {meta.label} · {items.length}
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {items.map((item, i) => (
                      <li
                        key={i}
                        style={{
                          padding: '6px 0 6px 18px',
                          position: 'relative',
                          fontSize: 14,
                          lineHeight: 1.55,
                          color: '#374151',
                          borderBottom: i === items.length - 1 ? 'none' : '1px solid #f9fafb',
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 13,
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: meta.color,
                          }}
                          aria-hidden
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
